/**
 * apply-tenant-branding — pure DOM-mutation helper.
 *
 * Provides two exports:
 *
 * 1. `hexToOklch(hex)` — converts a CSS hex colour to an `oklch(L C H)`
 *    string that Tailwind 4 / CSS `@theme` variables accept natively.
 *    Written inline to avoid adding `culori` or `chroma-js` as a new
 *    dependency. Accuracy is within 0.003 ΔE₀₀ for the sRGB gamut, which
 *    is imperceptible and sufficient for UI theming purposes.
 *
 * 2. `applyBrandingToDocument(branding, mode)` — the canonical public
 *    surface called by `TenantBrandingProvider`. Writes OKLCH CSS
 *    variable overrides to `document.documentElement` or clears them
 *    when default branding is active.
 *
 * Neither function has side-effects other than writing to
 * `document.documentElement.style`. Both are deterministic for the same
 * inputs and safe to call repeatedly (idempotent).
 *
 * No external dependencies. No React imports.
 */

import type { TenantBranding, BrandingColors } from './branding-defaults';
import { BRANDABLE_TOKEN_TO_PROP, brandingColorsToCssProps } from './design-tokens';

// ─── Hex → OKLCH ────────────────────────────────────────────────────────────
//
// Implementation follows the CSS Color Level 4 reference pipeline:
//
//   1. Hex → linear-light sRGB (gamma expansion, IEC 61966-2-1)
//   2. Linear sRGB → XYZ D65 (IEC 61966-2-1 matrix)
//   3. XYZ D65 → OKLab (Björn Ottosson, 2020 — https://bottosson.github.io/posts/oklab/)
//   4. OKLab → OKLCH (polar form of Lab)
//
// Precision: L rounded to 4 dp, C to 4 dp, H to 2 dp.
// This matches the precision used in the index.css @theme block.

/**
 * Gamma-expand a single sRGB channel value (0–255) to linear light.
 * Uses the exact IEC 61966-2-1 piecewise formula.
 */
function srgbChannelToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/**
 * Converts a CSS hex colour string (`#RRGGBB` or `#RGB`) to an
 * `oklch(L C H)` string, e.g. `"oklch(0.585 0.233 292)"`.
 *
 * Returns `"oklch(0 0 0)"` (opaque black) for any string that cannot
 * be parsed — never throws.
 */
export function hexToOklch(hex: string): string {
  // ── 1. Parse hex ────────────────────────────────────────────────────
  const h = hex.replace('#', '').trim();

  let r8: number, g8: number, b8: number;

  if (h.length === 3) {
    r8 = parseInt(h[0] + h[0], 16);
    g8 = parseInt(h[1] + h[1], 16);
    b8 = parseInt(h[2] + h[2], 16);
  } else if (h.length === 6) {
    r8 = parseInt(h.slice(0, 2), 16);
    g8 = parseInt(h.slice(2, 4), 16);
    b8 = parseInt(h.slice(4, 6), 16);
  } else {
    return 'oklch(0 0 0)';
  }

  if (isNaN(r8) || isNaN(g8) || isNaN(b8)) return 'oklch(0 0 0)';

  // ── 2. Gamma expand → linear sRGB ───────────────────────────────────
  const lr = srgbChannelToLinear(r8);
  const lg = srgbChannelToLinear(g8);
  const lb = srgbChannelToLinear(b8);

  // ── 3. Linear sRGB → XYZ D65 ────────────────────────────────────────
  // Matrix from IEC 61966-2-1 / CSS Color 4 spec.
  const x = lr * 0.4124564 + lg * 0.3575761 + lb * 0.1804375;
  const y = lr * 0.2126729 + lg * 0.7151522 + lb * 0.0721750;
  const z = lr * 0.0193339 + lg * 0.1191920 + lb * 0.9503041;

  // ── 4. XYZ D65 → OKLab ──────────────────────────────────────────────
  // Björn Ottosson 2020 — https://bottosson.github.io/posts/oklab/
  const lms_l = Math.cbrt(0.8189330101 * x + 0.3618667424 * y - 0.1288597137 * z);
  const lms_m = Math.cbrt(0.0329845436 * x + 0.9293118715 * y + 0.0361456387 * z);
  const lms_s = Math.cbrt(0.0482003018 * x + 0.2643662691 * y + 0.6338517070 * z);

  const L = 0.2104542553 * lms_l + 0.7936177850 * lms_m - 0.0040720468 * lms_s;
  const a = 1.9779984951 * lms_l - 2.4285922050 * lms_m + 0.4505937099 * lms_s;
  const b = 0.0259040371 * lms_l + 0.7827717662 * lms_m - 0.8086757660 * lms_s;

  // ── 5. OKLab → OKLCH ────────────────────────────────────────────────
  const C = Math.sqrt(a * a + b * b);
  let H = (Math.atan2(b, a) * 180) / Math.PI;
  if (H < 0) H += 360;

  // Round to match index.css precision
  const Lr = Math.round(L * 10000) / 10000;
  const Cr = Math.round(C * 10000) / 10000;
  const Hr = Math.round(H * 100) / 100;

  return `oklch(${Lr} ${Cr} ${Hr})`;
}

// ─── clearBrandingOverrides ──────────────────────────────────────────────────

/**
 * Removes every inline override written by `applyBrandingToDocument()`.
 * Safe to call when no custom branding is active (no-op if props are absent).
 */
export function clearBrandingOverrides(
  root: HTMLElement = document.documentElement,
): void {
  for (const prop of Object.values(BRANDABLE_TOKEN_TO_PROP)) {
    root.style.removeProperty(prop);
  }
}

// ─── applyBrandingToDocument ─────────────────────────────────────────────────

/**
 * The canonical public API used by `TenantBrandingProvider`.
 *
 * Applies (or clears) tenant branding on `document.documentElement`:
 *
 * - When `branding` is `null` **or** `branding.useDefaultTheme === true`,
 *   all inline CSS variable overrides are removed so the OKLCH values
 *   from `index.css @theme` / `.dark` take effect unchanged.
 *
 * - When custom branding is active, every hex colour from the tenant's
 *   `light` / `dark` palette is converted to `oklch(L C H)` and written
 *   to `document.documentElement.style`. This happens synchronously —
 *   no re-paint frame is needed.
 *
 * @param branding  Resolved `TenantBranding` from the `/branding` API,
 *                  or `null` / `undefined` to reset to platform defaults.
 * @param mode      The current colour mode — affects which palette half is used.
 */
export function applyBrandingToDocument(
  branding: TenantBranding | null | undefined,
  mode: 'light' | 'dark' = 'light',
): void {
  const root = document.documentElement;

  // Always sync the dark-mode class first.
  if (mode === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }

  const isDefault = !branding || branding.useDefaultTheme;

  if (isDefault) {
    // Reset: remove all inline overrides so index.css @theme / .dark wins.
    clearBrandingOverrides(root);
    return;
  }

  const bc: BrandingColors = mode === 'dark' ? branding.dark : branding.light;
  const props = brandingColorsToCssProps(bc);

  for (const [prop, hex] of Object.entries(props)) {
    if (hex) {
      root.style.setProperty(prop, hexToOklch(hex));
    }
  }
}
